import { Text, Title } from '@components/v2/ui';
import {
	ActionIcon,
	Avatar,
	Flex,
	Group,
	Image,
	Modal,
	Stack,
} from '@mantine/core';
import classes from './Details.module.css';

// Images
import BrandDetailsLogo from '@assets/images/brands-details.svg';

import {
	IconQuestionMark,
	IconX,
} from '@tabler/icons-react';

import { StatusSwitch } from '@components/v2/ui/StatusSwitch';
import { NOT_APPLICABLE } from '@config/constants/common';
import { getCountryName } from '@utils/countries';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Offsets } from './Offsets';

export const Details = (props: {
	data: any;
	opened: boolean;
	onClose: () => void;
}) => {
	const { data, opened, onClose } = props;

	const { name, brand } = data;

	const { t } = useTranslation();

	return (
		<Modal
			classNames={classes}
			opened={opened}
			onClose={onClose}
			size="auto"
			withCloseButton={false}
		>
			{/*Header */}
			<Flex
				align="center"
				gap="16px"
				justify="space-between"
			>
				<Group
					wrap="nowrap"
					gap="var(--mantine-color-spacing-sm)"
				>
					<Image
						h="48px"
						w="48px"
						src={BrandDetailsLogo}
						fit="contain"
					/>
					<Stack gap="0px">
						<Title
							order={6}
							c="var(--mantine-color-igluPrimary-9)"
						>
							{name}
						</Title>
						<Text
							c="var(--mantine-color-igluTextSecondary-9)"
							variant="body2"
						>
							{t('global.brand')}{' '}
							<Text
								c="var(--mantine-color-igluTextPrimary-9)"
								variant="button"
								span
							>
								{brand}
							</Text>
						</Text>
					</Stack>
				</Group>
				<ActionIcon
					color="var(--mantine-color-igluBorder-9)"
					radius={'48px'}
					variant="outline"
					size={'48px'}
					onClick={onClose}
				>
					<IconX
						color="var(--mantine-color-igluPrimary-9)"
						size={'24px'}
					/>
				</ActionIcon>
			</Flex>
			{/*Body */}
			<Flex
				wrap={'wrap'}
				mt="48px"
				justify="space-between"
				gap={'24px'}
			>
				{/*Brand details */}
				<Flex direction={'column'} flex={1}>
					{/*Title */}
					<Text
						c="var(--mantine-color-igluPrimary-9)"
						variant="subtitle1"
						mb={'24px'}
					>
						{t('global.brandDetails')}
					</Text>
					{/*Content */}
					<Flex direction={'column'} gap={'8px'}>
						{/*obfuscateIPFS */}
						<Flex gap="8px" align={'center'}>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextSecondary-9)"
									variant="body2"
								>
									{t('global.obfuscateIPFS')}
								</Text>
							</Flex>
							<Flex flex={'1'}>
								<Group gap="4px">
									<Text
										className={clsx(
											classes.dot,
											!data.obfuscateIPFS
												? classes.successColor
												: classes.errorColor
										)}
									>
										●
									</Text>

									<Text
										className={clsx(
											classes.textObfuscateIPFS,
											!data.obfuscateIPFS
												? classes.successColor
												: classes.errorColor
										)}
									>
										{!data.obfuscateIPFS
											? t(`global.shared`)
											: t(`global.notShared`)}
									</Text>
								</Group>
							</Flex>
						</Flex>
						{/*status */}
						<Flex gap="8px" align={'center'}>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextSecondary-9)"
									variant="body2"
								>
									{t('global.status')}
								</Text>
							</Flex>
							<Flex flex={'1'}>
								<StatusSwitch
									inActiveLabel={t('global.unverified')}
									activeLabel={t('global.verified')}
									active={data?.verified}
									readonly={true}
								/>
							</Flex>
						</Flex>
						{/*legalIdentifier */}
						{data?.legalIdentifier &&
							data?.legalIdentifier !==
								NOT_APPLICABLE && (
								<Flex gap="8px" align={'center'}>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextSecondary-9)"
											variant="body2"
										>
											{t('global.legalIdentifier')}
										</Text>
									</Flex>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextPrimary-9)"
											variant="body2-medium"
										>
											{data.legalIdentifier}
										</Text>
									</Flex>
								</Flex>
							)}
						{/*location */}
						<Flex gap="8px" align={'center'}>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextSecondary-9)"
									variant="body2"
								>
									{t('global.location')}
								</Text>
							</Flex>
							<Flex
								align={'center'}
								gap={'5px'}
								flex={'1'}
							>
								<Avatar
									className={classes.logoCountry}
									radius={'xl'}
									src={`https://flagcdn.com/w40/${data?.location?.countryCode?.toLowerCase()}.png`}
									size={'xs'}
								>
									<IconQuestionMark />
								</Avatar>
								<Text
									c="var(--mantine-color-igluTextPrimary-9)"
									variant="body2-medium"
								>
									{`${getCountryName(
										data?.location?.countryCode || ''
									)} / ${data?.location?.place ? data?.location?.place : NOT_APPLICABLE}`}
								</Text>
							</Flex>
						</Flex>
						{/*createdAt */}
						<Flex gap="8px" align={'center'}>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextSecondary-9)"
									variant="body2"
								>
									{t('global.createdAt')}
								</Text>
							</Flex>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextPrimary-9)"
									variant="body2-medium"
								>
									{data?.createdAt}
								</Text>
							</Flex>
						</Flex>
						{/*updatedAt */}
						<Flex gap="8px" align={'center'}>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextSecondary-9)"
									variant="body2"
								>
									{t('global.updatedAt')}
								</Text>
							</Flex>
							<Flex flex={'1'}>
								<Text
									c="var(--mantine-color-igluTextPrimary-9)"
									variant="body2-medium"
								>
									{data?.updatedAt}
								</Text>
							</Flex>
						</Flex>
						{/*verifiedAt */}
						{data?.verifiedAt &&
							data?.verifiedAt !== NOT_APPLICABLE && (
								<Flex gap="8px" align={'center'}>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextSecondary-9)"
											variant="body2"
										>
											{t('global.verifiedAt')}
										</Text>
									</Flex>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextPrimary-9)"
											variant="body2-medium"
										>
											{data?.verifiedAt}
										</Text>
									</Flex>
								</Flex>
							)}
						{/*verifiedBy */}
						{data?.verifiedBy &&
							data?.verifiedBy !== NOT_APPLICABLE && (
								<Flex gap="8px" align={'center'}>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextSecondary-9)"
											variant="body2"
										>
											{t('global.verifiedBy')}
										</Text>
									</Flex>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextPrimary-9)"
											variant="body2-medium"
										>
											{data?.verifiedBy}
										</Text>
									</Flex>
								</Flex>
							)}
						{/*contactName */}
						{data?.contactName &&
							data?.contactName !== NOT_APPLICABLE && (
								<Flex gap="8px" align={'center'}>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextSecondary-9)"
											variant="body2"
										>
											{t('global.contactName')}
										</Text>
									</Flex>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextPrimary-9)"
											variant="body2-medium"
										>
											{data?.contactName}
										</Text>
									</Flex>
								</Flex>
							)}
						{/*contactEmail */}
						{data?.contactEmail &&
							data?.contactEmail !== NOT_APPLICABLE && (
								<Flex gap="8px" align={'center'}>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextSecondary-9)"
											variant="body2"
										>
											{t('global.contactEmail')}
										</Text>
									</Flex>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextPrimary-9)"
											variant="body2-medium"
										>
											{data?.contactEmail}
										</Text>
									</Flex>
								</Flex>
							)}
						{/*contactPhone */}
						{data?.contactPhone &&
							data?.contactPhone !== NOT_APPLICABLE && (
								<Flex gap="8px" align={'center'}>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextSecondary-9)"
											variant="body2"
										>
											{t('global.contactPhone')}
										</Text>
									</Flex>
									<Flex flex={'1'}>
										<Text
											c="var(--mantine-color-igluTextPrimary-9)"
											variant="body2-medium"
										>
											{data?.contactPhone}
										</Text>
									</Flex>
								</Flex>
							)}
					</Flex>
				</Flex>
				{/*Offsets */}
				<Flex
					flex={'2'}
					direction={'column'}
					miw={'350px'}
				>
					{/*Title */}
					<Text
						c="var(--mantine-color-igluPrimary-9)"
						variant="subtitle1"
						mb={'24px'}
					>
						{t('sidebar.offsets')}
					</Text>
					{/*Content */}
					<Flex
						className={classes.containerProject}
						direction={'column'}
						gap={'8px'}
					>
						<Offsets offsetId={data?.id ?? ''} />
					</Flex>
				</Flex>
			</Flex>
		</Modal>
	);
};
